import React, { useState, useMemo } from 'react';
import { UsersIcon, SunIcon, CalendarClockIcon, PackageIcon, KeyIcon, SearchIcon, PlusIcon, CheckIcon, XIcon, AlertTriangleIcon, FileTextIcon, FileCheckIcon, LockIcon, ChevronRightIcon, BuildingIcon, UserIcon, MinusCircleIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { ActivityRegistrations } from './loisirs/ActivityRegistrations';
import { BookingsSection } from './loisirs/BookingsSection';
import { paymentMeta } from './loisirs/bookingHelpers';
import { ACTIVITIES, CAMPS, CAMP_REGISTRATIONS, EQUIPMENT, EQUIPMENT_LOANS, ACCESS_POINTS, ACCESS_EVENTS } from '../data/loisirs';
import { BOOKING_FILES, CONTRACTS } from '../data/windsor';
import { resolvePayment, type FileStatus } from '../types/loisirs';
type Section = 'inscriptions' | 'camps' | 'reservations' | 'equipements' | 'acces';

/** Small stat shown at the top of each operational section. */
function Stat({
  label,
  value,
  hint,
  tone = 'neutral'
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'neutral' | 'warning' | 'positive';
}) {
  return <div className="bg-white rounded-xl border border-slate-200 px-4 py-3.5" data-id="element-3443" data-mp="18n3hl25q6519">
      <p className="text-xs font-medium text-slate-500" data-id="element-3444" data-mp="tvkz8xpe7yeq">{label}</p>
      <p className={`text-2xl font-bold font-heading mt-0.5 ${tone === 'warning' ? 'text-amber-600' : tone === 'positive' ? 'text-emerald-600' : 'text-[#0F172A]'}`} data-id="element-3445" data-mp="yufi1znd1myu">
        {value}
      </p>
      {hint && <p className="text-xs text-slate-400 mt-0.5" data-id="element-3446" data-mp="axrxhyqi9uix">{hint}</p>}
    </div>;
}
export function LoisirsGerer() {
  const {
    t,
    lang
  } = useLanguage();
  const locale = lang === 'fr' ? 'fr-CA' : 'en-CA';
  const [section, setSection] = useState<Section>('inscriptions');
  const [query, setQuery] = useState('');
  const [creatingActivity, setCreatingActivity] = useState(false);
  /** Access point the log is filtered on, or null for every point. */
  const [accessPointFilter, setAccessPointFilter] = useState<string | null>(null);
  const activePoint = ACCESS_POINTS.find(p => p.id === accessPointFilter);

  /** Access log, narrowed by the selected reader and the search box. */
  const accessLog = ACCESS_EVENTS.filter(e => {
    if (accessPointFilter && e.accessPointId !== accessPointFilter) return false;
    const q = query.trim().toLowerCase();
    return q ? e.citizen.toLowerCase().includes(q) || e.cardId.toLowerCase().includes(q) : true;
  });
  const [creatingBooking, setCreatingBooking] = useState(false);
  const money = (value: number) => value.toLocaleString(locale, {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0
  });
  const sections: {
    id: Section;
    label: string;
    icon: typeof UsersIcon;
    count: number;
  }[] = [{
    id: 'inscriptions',
    label: t('Inscriptions', 'Registrations'),
    icon: UsersIcon,
    count: ACTIVITIES.length
  }, {
    id: 'camps',
    label: t('Camps de jour', 'Day camps'),
    icon: SunIcon,
    count: CAMP_REGISTRATIONS.filter(r => r.healthForm !== 'complete').length
  }, {
    id: 'reservations',
    label: t('Réservations', 'Bookings'),
    icon: CalendarClockIcon,
    count: BOOKING_FILES.filter(b => b.status === 'brouillon').length + CONTRACTS.filter(c => c.status === 'a_generer' || c.status === 'en_retard').length
  }, {
    id: 'equipements',
    label: t('Équipements', 'Equipment'),
    icon: PackageIcon,
    count: EQUIPMENT_LOANS.filter(l => l.status === 'overdue').length
  }, {
    id: 'acces',
    label: t('Contrôle d’accès', 'Access control'),
    icon: KeyIcon,
    count: ACCESS_EVENTS.filter(e => e.result === 'denied').length
  }];
  const fileMeta: Record<FileStatus, {
    label: string;
    className: string;
  }> = {
    complete: {
      label: t('Complète', 'Complete'),
      className: 'bg-emerald-50 text-emerald-700 border-emerald-200'
    },
    incomplete: {
      label: t('Incomplète', 'Incomplete'),
      className: 'bg-amber-50 text-amber-700 border-amber-200'
    },
    missing: {
      label: t('Manquante', 'Missing'),
      className: 'bg-red-50 text-red-700 border-red-200'
    }
  };
  const searchPlaceholder: Record<Section, string> = {
    inscriptions: t('Rechercher une activité…', 'Search activities…'),
    camps: t('Rechercher un enfant ou un parent…', 'Search a child or guardian…'),
    reservations: t('Rechercher un client, un n° ou une ressource…', 'Search a client, number or resource…'),
    equipements: t('Rechercher un équipement…', 'Search equipment…'),
    acces: t('Rechercher un citoyen ou une carte…', 'Search a citizen or card…')
  };
  const primaryAction: Record<Section, string> = {
    inscriptions: t('Nouvelle activité', 'New activity'),
    camps: t('Inscrire un enfant', 'Register a child'),
    reservations: t('Nouvelle réservation', 'New booking'),
    equipements: t('Nouveau prêt', 'New loan'),
    acces: t('Émettre un accès', 'Issue an access')
  };
  return <div className="space-y-6 animate-in fade-in duration-300" data-id="element-3447" data-mp="2uy6mbhk85e3n">
      {/* Section navigation */}
      <div className="flex flex-wrap items-center gap-2" data-id="element-3448" data-mp="2h34jx4el66vl">
        {sections.map(s => {
        const Icon = s.icon;
        const active = section === s.id;
        return <button key={s.id} onClick={() => {
          setSection(s.id);
          setQuery('');
        }} className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium border transition-colors ${active ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`} data-id="element-3449" data-mp="38sprg0rjyvpn">
              <Icon className={`w-4 h-4 ${active ? 'text-[#4F46E5]' : 'text-slate-400'}`} data-id="element-3450" data-mp="dpb241odbhr" />
              {s.label}
              {s.count > 0 && <span className={`text-xs font-bold ${active ? 'text-slate-300' : 'text-slate-400'}`} data-id="element-3451" data-mp="67kqzw9g634h">
                  {s.count}
                </span>}
            </button>;
      })}
      </div>

      {/* Toolbar — hidden while a creation form is open. */}
      {!creatingActivity && !creatingBooking && <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-id="element-3452" data-mp="3io6hj6kh5niv">
        <div className="relative flex-1 sm:max-w-sm" data-id="element-3453" data-mp="3emcj74xr4vlv">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" data-id="element-3454" data-mp="3en4gqlawt3es" />
          <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder={searchPlaceholder[section]} aria-label={searchPlaceholder[section]} className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-1 focus:ring-[#4F46E5] focus:border-[#4F46E5]" data-id="element-3455" data-mp="39p2whx1qyy1q" />
        </div>
        <button onClick={() => {
        if (section === 'inscriptions') setCreatingActivity(true);
        if (section === 'reservations') setCreatingBooking(true);
      }} className="shrink-0 inline-flex items-center gap-2 px-4 py-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-semibold rounded-lg transition-colors shadow-sm" data-id="element-3456" data-mp="2zjbd94yqib7c">
          <PlusIcon className="w-4 h-4" data-id="element-3457" data-mp="2brmdx4nhwmgw" />
          {primaryAction[section]}
        </button>
      </div>}

      {/* ---------------------------------------------------- INSCRIPTIONS */}
      {section === 'inscriptions' && <ActivityRegistrations query={query} creating={creatingActivity} onCloseCreate={() => setCreatingActivity(false)} data-id="element-3458" data-mp="1o4o60vdfymul" />}

      {/* ----------------------------------------------------------- CAMPS */}
      {section === 'camps' && <div className="space-y-4" data-id="element-3459" data-mp="338dr0yzehl9l">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-3460" data-mp="3395ko49ft8x8">
            <Stat label={t('Enfants inscrits', 'Children registered')} value={`${CAMPS.reduce((s, c) => s + c.registered, 0)}`} hint={`${CAMPS.reduce((s, c) => s + c.capacity, 0)} ${t('places', 'spots')}`} data-id="element-3461" data-mp="3h7lvo7ec1ll6" />
            <Stat label={t('Fiches santé à compléter', 'Health forms to complete')} value={`${CAMPS.reduce((s, c) => s + (c.registered - c.healthFormsComplete), 0)}`} tone="warning" hint={t('obligatoires avant le début', 'mandatory before start')} data-id="element-3462" data-mp="3q6qocx37qkpf" />
            <Stat label={t('Relevés 24 émis', 'RL-24 slips issued')} value={`${CAMPS.reduce((s, c) => s + c.rl24Issued, 0)}`} tone="positive" hint={t('transmis à Revenu Québec', 'filed with Revenu Québec')} data-id="element-3463" data-mp="3v1mmmez1m4az" />
            <Stat label={t('Camps actifs', 'Active camps')} value={`${CAMPS.length}`} hint={t('été 2026', 'summer 2026')} data-id="element-3464" data-mp="sen3z3x1vxpf" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3" data-id="element-3465" data-mp="ifftcohpah60">
            {CAMPS.map(camp => {
          const missing = camp.registered - camp.healthFormsComplete;
          return <div key={camp.id} className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-3466" data-mp="8ol89ng6xnm5">
                  <h3 className="text-base font-semibold text-[#0F172A]" data-id="element-3467" data-mp="3vv40d5rwag7l">
                    {camp.name[lang]}
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5" data-id="element-3468" data-mp="4osu8v812p9e">
                    {camp.venue[lang]} · {camp.ageRange} {t('ans', 'yrs')} ·{' '}
                    {camp.weeks} {t('semaines', 'weeks')}
                  </p>
                  <div className="mt-3 space-y-2" data-id="element-3469" data-mp="3qxlszjtj091p">
                    <div data-id="element-3470" data-mp="hehlkbns58hh">
                      <div className="flex items-center justify-between text-xs mb-1" data-id="element-3471" data-mp="hdy5l0howu63">
                        <span className="text-slate-500" data-id="element-3472" data-mp="mfdr0tns52p0">
                          {t('Inscriptions', 'Registrations')}
                        </span>
                        <span className="font-medium text-slate-700" data-id="element-3473" data-mp="cg6bq0xhixz9">
                          {camp.registered} / {camp.capacity}
                        </span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-3474" data-mp="1553su0pymoqf">
                        <div className="h-full bg-[#4F46E5]" style={{
                    width: `${camp.registered / camp.capacity * 100}%`
                  }} data-id="element-3475" data-mp="156y4eofcd4hu" />
                      </div>
                    </div>
                    <div className="flex items-center gap-3 pt-1" data-id="element-3476" data-mp="1nvl8p4pp1pud">
                      <span className="inline-flex items-center gap-1.5 text-xs text-slate-600" data-id="element-3477" data-mp="1nwwa4xvo2lib">
                        <FileTextIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-3478" data-mp="38k5s5tvpadry" />
                        {camp.healthFormsComplete}
                        {missing > 0 && <span className="text-amber-600 font-medium" data-id="element-3479" data-mp="3ias1558ww61o">
                            (−{missing})
                          </span>}
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-xs text-slate-600" data-id="element-3480" data-mp="2po7902km3px2">
                        <FileCheckIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-3481" data-mp="2kp31dgdc608u" />
                        {camp.rl24Issued} {t('RL-24', 'RL-24')}
                      </span>
                    </div>
                  </div>
                </div>;
        })}
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-3482" data-mp="dwtwupy1usd6">
            <div className="px-5 py-4 border-b border-slate-100" data-id="element-3483" data-mp="9wje0cp5s7gw">
              <h3 className="text-base font-semibold text-[#0F172A]" data-id="element-3484" data-mp="9ycndzr5p5sl">
                {t('Dossiers d’inscription', 'Registration files')}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5" data-id="element-3485" data-mp="37oibx3vo8xkj">
                {t('Fiche santé et relevé 24 par enfant', 'Health form and RL-24 slip per child')}
              </p>
            </div>
            <div className="overflow-x-auto" data-id="element-3486" data-mp="3gnx18003r23g">
              <table className="w-full text-sm text-left" data-id="element-3487" data-mp="2k11vozlokt9y">
                <thead className="text-xs text-slate-500 uppercase bg-slate-50" data-id="element-3488" data-mp="2oz3gpq2o32o2">
                  <tr data-id="element-3489" data-mp="2owplnh7zlqgi">
                    <th className="px-5 py-3" data-id="element-3490" data-mp="2t0xb5gm9rafs">{t('Enfant', 'Child')}</th>
                    <th className="px-5 py-3" data-id="element-3491" data-mp="2sykowr9hadwx">{t('Camp', 'Camp')}</th>
                    <th className="px-5 py-3" data-id="element-3492" data-mp="2xsyl2vvysp2n">{t('Fiche santé', 'Health form')}</th>
                    <th className="px-5 py-3" data-id="element-3493" data-mp="218g6uz96y2yv">{t('Relevé 24', 'RL-24')}</th>
                    <th className="px-5 py-3" data-id="element-3494" data-mp="25akvfjks89en">{t('Montant', 'Amount')}</th>
                    <th className="px-5 py-3" data-id="element-3495" data-mp="258qcubwzgtdq">{t('Suivi', 'Follow-up')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-id="element-3496" data-mp="1srz0ra11zbp4">
                  {CAMP_REGISTRATIONS.filter(r => {
                const q = query.trim().toLowerCase();
                return q ? r.childName.toLowerCase().includes(q) || r.guardian.toLowerCase().includes(q) : true;
              }).map(reg => {
                const health = fileMeta[reg.healthForm];
                return <tr key={reg.id} className="hover:bg-slate-50/60" data-id="element-3497" data-mp="152muahx41hqi">
                        <td className="px-5 py-3.5" data-id="element-3498" data-mp="150tmv0eoathg">
                          <p className="font-medium text-slate-900" data-id="element-3499" data-mp="112cd093vb4wc">
                            {reg.childName}
                          </p>
                          <p className="text-xs text-slate-500" data-id="element-3500" data-mp="w4at5lymb1jt">
                            {reg.childAge} {t('ans', 'yrs')} · {reg.guardian}
                          </p>
                        </td>
                        <td className="px-5 py-3.5 text-slate-600" data-id="element-3501" data-mp="2r7h90k742i9k">
                          <p data-id="element-3502" data-mp="2w364abc3ld02">{reg.campName[lang]}</p>
                          <p className="text-xs text-slate-400" data-id="element-3503" data-mp="2hfurk646jmyd">
                            {reg.weeks} {t('semaines', 'weeks')}
                          </p>
                        </td>
                        <td className="px-5 py-3.5" data-id="element-3504" data-mp="2mcbif82rkff5">
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${health.className}`} data-id="element-3505" data-mp="28e3ol910xu87">
                            {health.label}
                          </span>
                        </td>
                        <td className="px-5 py-3.5" data-id="element-3506" data-mp="23gv0ysx3wxzk">
                          {reg.rl24 === 'issued' ? <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700" data-id="element-3507" data-mp="3sw1i6f6fe2h4">
                              <CheckIcon className="w-3.5 h-3.5" data-id="element-3508" data-mp="1nnorscsiwhw" />
                              {t('Émis', 'Issued')}
                            </span> : reg.rl24 === 'pending' ? <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700" data-id="element-3509" data-mp="1h5a7k8o62tcp">
                              <AlertTriangleIcon className="w-3.5 h-3.5" data-id="element-3510" data-mp="1vy3v8hdlefsg" />
                              {t('À émettre', 'To issue')}
                            </span> : <span className="inline-flex items-center gap-1 text-xs text-slate-400" data-id="element-3511" data-mp="24ysaffeq2m6x">
                              <MinusCircleIcon className="w-3.5 h-3.5" data-id="element-3512" data-mp="2ev54iee6j227" />
                              {t('Non admissible', 'Not eligible')}
                            </span>}
                        </td>
                        <td className="px-5 py-3.5" data-id="element-3513" data-mp="kgcc0o8lereh">
                          <p className="font-medium text-slate-900" data-id="element-3514" data-mp="kj8caj7wxs63">
                            {money(reg.amount)}
                          </p>
                          <span className={`inline-flex mt-0.5 px-2 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap ${paymentMeta(resolvePayment(reg).status).className}`} data-id="element-3515" data-mp="156yfmshia73h">
                            {paymentMeta(resolvePayment(reg).status)[lang]}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 max-w-xs" data-id="element-3516" data-mp="1o4slixnc82hn">
                          {reg.alerts.length === 0 ? <span className="text-xs text-slate-400" data-id="element-3517" data-mp="1s49sjviex7ox">—</span> : <ul className="space-y-1" data-id="element-3518" data-mp="21wfofgbsc99e">
                              {reg.alerts.map((alert, i) => <li key={i} className="flex items-start gap-1.5 text-xs text-amber-700" data-id="element-3519" data-mp="26wbk8sabwudv" data-mpi={i}>
                                  <AlertTriangleIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-3520" data-mp="7jkshu1xmgy" />
                                  {alert[lang]}
                                </li>)}
                            </ul>}
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>
          </div>
        </div>}

      {/* ---------------------------------------------------- RÉSERVATIONS */}
      {section === 'reservations' && <BookingsSection query={query} creating={creatingBooking} onCloseCreate={() => setCreatingBooking(false)} data-id="element-3521" data-mp="97g3m2trfr43" />}

      {/* ----------------------------------------------------- ÉQUIPEMENTS */}
      {section === 'equipements' && <div className="space-y-4" data-id="element-3522" data-mp="wxiska2dogyh">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-3523" data-mp="wwhhi5ld3ugr">
            <Stat label={t('Articles en prêt', 'Items on loan')} value={`${EQUIPMENT.reduce((s, e) => s + e.onLoan, 0)}`} hint={t('auprès des organismes', 'with organizations')} data-id="element-3524" data-mp="5a7omtbi05g8" />
            <Stat label={t('Retours en retard', 'Overdue returns')} value={`${EQUIPMENT_LOANS.filter(l => l.status === 'overdue').length}`} tone="warning" hint={t('relance à faire', 'follow-up needed')} data-id="element-3525" data-mp="3sfyonastdig5" />
            <Stat label={t('Dépôts détenus', 'Deposits held')} value={money(EQUIPMENT_LOANS.filter(l => l.status !== 'returned').reduce((s, l) => s + (EQUIPMENT.find(e => e.name.fr === l.equipment.fr)?.deposit ?? 0), 0))} hint={t('remboursables au retour', 'refundable on return')} data-id="element-3526" data-mp="t021havthu0h" />
            <Stat label={t('Catégories', 'Categories')} value={`${new Set(EQUIPMENT.map(e => e.category.fr)).size}`} hint={t('au catalogue de prêt', 'in the loan catalogue')} data-id="element-3527" data-mp="2x5bpnjdjk29t" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start" data-id="element-3528" data-mp="365qc4m28rnew">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-3529" data-mp="31887h7v9bucp">
              <div className="px-5 py-4 border-b border-slate-100" data-id="element-3530" data-mp="2dimbsue3fx9g">
                <h3 className="text-base font-semibold text-[#0F172A]" data-id="element-3531" data-mp="29g96u87683dq">
                  {t('Inventaire', 'Inventory')}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5" data-id="element-3532" data-mp="2n85u9yxw9p3s">
                  {t('Prêt réservé aux organismes reconnus', 'Loans reserved for recognized organizations')}
                </p>
              </div>
              <ul className="divide-y divide-slate-100" data-id="element-3533" data-mp="1psjnmpdavbg4">
                {EQUIPMENT.filter(e => {
              const q = query.trim().toLowerCase();
              return q ? e.name[lang].toLowerCase().includes(q) : true;
            }).map(item => <li key={item.id} className="px-5 py-3.5 flex items-center gap-4 hover:bg-slate-50/60" data-id="element-3534" data-mp="h6wfuewqsiya">
                    <div className="min-w-0 flex-1" data-id="element-3535" data-mp="r5c7qk3lfw8u">
                      <p className="text-sm font-medium text-slate-800" data-id="element-3536" data-mp="14y1uwruuz77j">
                        {item.name[lang]}
                      </p>
                      <p className="text-xs text-slate-400" data-id="element-3537" data-mp="10vyj224a20lq">
                        {item.category[lang]}
                        {item.deposit > 0 && <>
                            {' · '}
                            {t('dépôt ', 'deposit ')}
                            {money(item.deposit)}
                          </>}
                      </p>
                    </div>
                    <div className="text-right shrink-0" data-id="element-3538" data-mp="3br4a2uc85oqm">
                      <p className={`text-sm font-bold ${item.available === 0 ? 'text-red-600' : 'text-[#0F172A]'}`} data-id="element-3539" data-mp="1r81uxpt7zyy4">
                        {item.available} / {item.total}
                      </p>
                      <p className="text-[11px] text-slate-400" data-id="element-3540" data-mp="1mepkcjldlsod">
                        {t('disponibles', 'available')}
                      </p>
                    </div>
                  </li>)}
              </ul>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-3541" data-mp="yi317h42zdn4">
              <div className="px-5 py-4 border-b border-slate-100" data-id="element-3542" data-mp="ykfs4jrtgycb">
                <h3 className="text-base font-semibold text-[#0F172A]" data-id="element-3543" data-mp="13hoc8pwizcg7">
                  {t('Prêts en cours', 'Active loans')}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5" data-id="element-3544" data-mp="2xuxepxqnlxoy">
                  {t('Sorties et retours prévus', 'Check-outs and expected returns')}
                </p>
              </div>
              <ul className="divide-y divide-slate-100" data-id="element-3545" data-mp="2rex7iq71l09i">
                {EQUIPMENT_LOANS.map(loan => <li key={loan.id} className="px-5 py-3.5 hover:bg-slate-50/60" data-id="element-3546" data-mp="2mfklu8rwpz8i">
                    <div className="flex items-start justify-between gap-3" data-id="element-3547" data-mp="2mhnkfh74l0ou">
                      <div className="min-w-0" data-id="element-3548" data-mp="2ig21pf7wts6q">
                        <p className="text-sm font-medium text-slate-800" data-id="element-3549" data-mp="3f51b4xk8d00i">
                          {loan.quantity} × {loan.equipment[lang]}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5" data-id="element-3550" data-mp="3a7reawuihm2n">
                          {loan.organization}
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5" data-id="element-3551" data-mp="31dwfe6jzgrvg">
                          {t('Retour prévu le ', 'Due back on ')}
                          {new Date(loan.dueDate).toLocaleDateString(locale, {
                      day: 'numeric',
                      month: 'short'
                    })}
                        </p>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium border shrink-0 ${loan.status === 'overdue' ? 'bg-red-50 text-red-700 border-red-200' : loan.status === 'out' ? 'bg-indigo-50 text-[#4F46E5] border-indigo-100' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`} data-id="element-3552" data-mp="1kk7x0pvhmxmp">
                        {loan.status === 'overdue' ? t('En retard', 'Overdue') : loan.status === 'out' ? t('Sorti', 'Out') : t('Retourné', 'Returned')}
                      </span>
                    </div>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>}

      {/* ----------------------------------------------------------- ACCÈS */}
      {section === 'acces' && <div className="space-y-4" data-id="element-3553" data-mp="23m6zjk5jpky6">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3" data-id="element-3554" data-mp="2idpav4ngmrb7">
            {ACCESS_POINTS.map(point => {
          const rate = point.maxOccupancy ? Math.round(point.occupancy / point.maxOccupancy * 100) : 0;
          const selected = accessPointFilter === point.id;
          return <button key={point.id} onClick={() => setAccessPointFilter(selected ? null : point.id)} aria-pressed={selected} className={`text-left rounded-xl border p-5 transition-colors ${selected ? 'bg-white border-[#4F46E5] ring-1 ring-[#4F46E5]/20' : point.online ? 'bg-white border-slate-200 hover:border-slate-300' : 'bg-slate-50 border-slate-200 hover:border-slate-300'}`} data-id="element-3555" data-mp="1kri902ijjd2j">
                  <div className="flex items-start justify-between gap-2 mb-2" data-id="element-3556" data-mp="34m338hal4y9f">
                    <div className="min-w-0" data-id="element-3557" data-mp="38n6h4y92ok80">
                      <p className="text-sm font-semibold text-[#0F172A] truncate" data-id="element-3558" data-mp="38ny63q85zjpb">
                        {point.name[lang]}
                      </p>
                      <p className="text-xs text-slate-500" data-id="element-3559" data-mp="566j6q8vxpxz">{point.venue[lang]}</p>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border shrink-0 ${point.online ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`} data-id="element-3560" data-mp="3nkyylvy6jiou">
                      <span className={`w-1.5 h-1.5 rounded-full ${point.online ? 'bg-emerald-500' : 'bg-red-500'}`} data-id="element-3561" data-mp="xso6aejda384" />
                      {point.online ? t('En ligne', 'Online') : t('Hors ligne', 'Offline')}
                    </span>
                  </div>

                  {point.online ? <>
                      <div className="flex items-baseline gap-2" data-id="element-3562" data-mp="ev3p7tmug8y2">
                        <span className={`text-2xl font-bold font-heading ${rate >= 85 ? 'text-amber-600' : 'text-[#0F172A]'}`} data-id="element-3563" data-mp="a3d7v3phokx5">
                          {point.occupancy}
                        </span>
                        <span className="text-sm text-slate-400" data-id="element-3564" data-mp="3s9hv1cuw2jos">
                          / {point.maxOccupancy}
                        </span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-2" data-id="element-3565" data-mp="3nc7yld9317rg">
                        <div className={`h-full ${rate >= 85 ? 'bg-amber-400' : 'bg-[#4F46E5]'}`} style={{
                  width: `${rate}%`
                }} data-id="element-3566" data-mp="3d8b1u3wzol39" />
                      </div>
                      <p className="text-xs text-slate-400 mt-2" data-id="element-3567" data-mp="3s14tsotg6tot">
                        {point.entriesToday} {t('entrées', 'entries')} ·{' '}
                        <span className="text-amber-600 font-medium" data-id="element-3568" data-mp="2ze0kw8r8hv7r">
                          {point.deniedToday} {t('refus', 'denied')}
                        </span>
                      </p>
                    </> : <p className="text-xs text-slate-500 pt-2" data-id="element-3569" data-mp="395wm3soma1g7">
                      {t('Lecteur injoignable — accès géré manuellement au comptoir.', 'Reader unreachable — access handled manually at the counter.')}
                    </p>}
                </button>;
        })}
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-3570" data-mp="3qui5o8umgarl">
            <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-4 flex-wrap" data-id="element-3571" data-mp="8o3taec048vb">
              <div data-id="element-3572" data-mp="3mwie6ekatkf">
                <h3 className="text-base font-semibold text-[#0F172A]" data-id="element-3573" data-mp="3qk9kywjb3vz">
                  {t('Journal des accès', 'Access log')}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5" data-id="element-3574" data-mp="dlc762q49qqr">
                  {t('Validation de la carte citoyenne à l’entrée', 'Citizen card validation at the door')}
                </p>
              </div>
              {activePoint && <button onClick={() => setAccessPointFilter(null)} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-indigo-50 border border-indigo-100 text-sm font-medium text-[#3730A3] hover:bg-indigo-100 transition-colors" data-id="element-3575" data-mp="11cri2jvylgdm">
                  {activePoint.name[lang]}
                  <XIcon className="w-3.5 h-3.5" data-id="element-3576" data-mp="1jxxmcym5sry9" />
                </button>}
            </div>
            <div className="overflow-x-auto" data-id="element-3577" data-mp="1azll82no2brk">
              <table className="w-full text-sm text-left" data-id="element-3578" data-mp="1ayaje8ds7e33">
                <thead className="text-xs text-slate-500 uppercase bg-slate-50" data-id="element-3579" data-mp="waxty8kuso7f">
                  <tr data-id="element-3580" data-mp="w8uu6wtwfgpi">
                    <th className="px-5 py-3" data-id="element-3581" data-mp="111xsfi9mfec1">{t('Heure', 'Time')}</th>
                    <th className="px-5 py-3" data-id="element-3582" data-mp="1138wlj6zao3o">{t('Photo', 'Photo')}</th>
                    <th className="px-5 py-3" data-id="element-3583" data-mp="nbkl9i4k03nr">{t('Citoyen', 'Citizen')}</th>
                    <th className="px-5 py-3" data-id="element-3584" data-mp="sa5jxd45qfa3">{t('Installation', 'Venue')}</th>
                    <th className="px-5 py-3" data-id="element-3585" data-mp="dbbkrs3j3w2k">{t('Droit d’accès', 'Entitlement')}</th>
                    <th className="px-5 py-3" data-id="element-3586" data-mp="ian3v7rdbdnb">{t('Résultat', 'Result')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-id="element-3587" data-mp="e7afnufuvrpq">
                  {accessLog.map(event => <tr key={event.id} className="hover:bg-slate-50/60" data-id="element-3588" data-mp="j6mz057iqgnk">
                      <td className="px-5 py-3.5 font-medium text-slate-700 tabular-nums align-top" data-id="element-3589" data-mp="o45cm8whdk1w">
                        {event.time}
                      </td>
                      <td className="px-5 py-3.5" data-id="element-3590" data-mp="s45qnsfo3t8f">
                        {/* Large enough to confirm the person who just badged in. */}
                        <img src={event.photo} alt={t(`Photo de ${event.citizen} au dossier de sa carte citoyenne`, `Photo of ${event.citizen} on file with their citizen card`)} className={`w-20 h-20 rounded-lg object-cover bg-slate-100 border-2 ${event.result === 'granted' ? 'border-emerald-300' : 'border-red-300'}`} data-id="element-3591" data-mp="123wj7oko3y5v" />
                      </td>
                      <td className="px-5 py-3.5" data-id="element-3592" data-mp="30ckelvq37p2p">
                        <p className="font-semibold text-slate-900" data-id="element-3593" data-mp="2wbiab0hbcmtv">
                          {event.citizen}
                        </p>
                        <p className="text-xs text-slate-400 mt-0.5" data-id="element-3594" data-mp="2rf1m66a468gs">
                          {event.cardId}
                        </p>
                      </td>
                      <td className="px-5 py-3.5 text-slate-600" data-id="element-3595" data-mp="3k1mdj6qlakkc">
                        {event.venue[lang]}
                      </td>
                      <td className="px-5 py-3.5 text-slate-600" data-id="element-3596" data-mp="3f9bfk9fet8uf">
                        {event.pass[lang]}
                      </td>
                      <td className="px-5 py-3.5" data-id="element-3597" data-mp="blsx0rw9d93a">
                        {event.result === 'granted' ? <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border bg-emerald-50 text-emerald-700 border-emerald-200" data-id="element-3598" data-mp="6ut19pyvkx2d">
                            <CheckIcon className="w-3.5 h-3.5" data-id="element-3599" data-mp="uqo8pekbvnou" />
                            {t('Accès accordé', 'Access granted')}
                          </span> : <div data-id="element-3600" data-mp="l0uzgk7nrknr">
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border bg-red-50 text-red-700 border-red-200" data-id="element-3601" data-mp="l03151mof8ts">
                              <XIcon className="w-3.5 h-3.5" data-id="element-3602" data-mp="c06lfpi9lkav" />
                              {t('Accès refusé', 'Access denied')}
                            </span>
                            {event.reason && <p className="text-xs text-slate-500 mt-1" data-id="element-3603" data-mp="21h0toyzhlqx">
                                {event.reason[lang]}
                              </p>}
                          </div>}
                      </td>
                    </tr>)}
                </tbody>
              </table>
              {accessLog.length === 0 && <div className="py-14 text-center" data-id="element-3604" data-mp="3cx0gzu9hkw6g">
                  <p className="text-sm font-semibold text-slate-700" data-id="element-3605" data-mp="3cue0340ph4bk">
                    {t('Aucun passage enregistré', 'No entry recorded')}
                  </p>
                  <p className="text-xs text-slate-500 mt-1" data-id="element-3606" data-mp="3lw4191ke3hmj">
                    {activePoint ? t(`Aucun passage à « ${activePoint.name.fr} » pour le moment.`, `No entry at “${activePoint.name.en}” yet.`) : t('Ajustez votre recherche.', 'Adjust your search.')}
                  </p>
                </div>}
            </div>
          </div>
        </div>}
    </div>;
}